import { BreadcrumbProps, Card, CardBody, DataTable, Icon, InlineEditableSelect, InlineEditableText, Tooltip, highlightText } from '@aidkitorg/component-library'
import { ColumnDef } from '@tanstack/react-table'
import React, { useRef } from 'react'
import { useSettings } from '../../Contexts/SettingsContext'
import { useLocalizedStrings } from '../../Localization'
import { AdminPanelPage } from '../AdminPanelPage'
import { DataTableRow } from './types'
import { CATEGORY_OPTIONS, filterTargetFieldRows, getContrastTextColor, stringToColour } from "./utils"

// Render field name with monospace styling and search highlighting
function FieldNameRenderer({ fieldName, searchTerm }: { fieldName: string, searchTerm?: string }) {
  return (
    <code className="inline-block px-1.5 py-0.5 text-xs font-mono bg-gray-100 text-gray-800 border border-gray-200 rounded">
      {highlightText(fieldName, searchTerm || '')}
    </code>
  );
}

export const ManageFieldsPage = () => {
  const L = useLocalizedStrings()
  const { transformedFields, saveField, isFieldsLoading } = useSettings();
  const saveFieldRef = useRef(saveField);
  saveFieldRef.current = saveField;
  const categoryOptions = React.useMemo(() => [...CATEGORY_OPTIONS], []);

  const getRowId = React.useCallback(
    (row: DataTableRow) => row.targetField,
    []
  );

  const breadcrumbs: BreadcrumbProps['items'] = [
    {
      label: 'Settings'
    }
  ]

  const columns = React.useMemo((): ColumnDef<DataTableRow>[] => [
    {
      accessorKey: "targetField",
      header: L.admin_panel.settings.manage_fields_table.field_name,
      size: 200,
      cell: (info: any) => {
        const row = info.row.original as DataTableRow;
        const primaryField = info.getValue() as string;
        const relatedFields = row.relatedFields;

        return (
          <div className="flex flex-col gap-4 max-w-xs break-all min-w-24">
            <div className="flex-grow-0">
              <div className="text-xs text-gray-500 font-medium mb-1">{L.admin_panel.settings.manage_fields_table.target_field}</div>
              <FieldNameRenderer fieldName={primaryField} searchTerm={info.searchTerm} />
            </div>
            {relatedFields && relatedFields.length > 0 && (
              <div>
                <div className="text-xs text-gray-500 font-medium mb-1">{L.admin_panel.settings.manage_fields_table.related_fields}</div>
                <div className="flex flex-wrap gap-1">
                  {relatedFields.map((field, index) => (
                    <FieldNameRenderer key={index} fieldName={field} searchTerm={info.searchTerm} />
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "kind",
      header: L.admin_panel.settings.manage_fields_table.type,
      size: 200,
      cell: (info: any) => {
        const kind = info.getValue() as string | undefined;
        if (!kind) return '-';

        const backgroundColor = stringToColour(kind);
        const textColor = getContrastTextColor(backgroundColor);
        return (
          <span
            className="inline-block px-2 py-1 rounded text-sm font-medium"
            style={{ backgroundColor, color: textColor }}
          >
            {highlightText(kind, info.searchTerm || '')}
          </span>
        );
      },
    },
    {
      accessorKey: "category",
      header: "Category",
      size: 160,
      cell: (info: any) => {
        const row = info.row.original as DataTableRow;
        
        // Special handling for UID - just show a disabled state
        if (row.targetField === 'uid') {
          return (
            <div className="flex items-center h-10 text-gray-700 text-sm">
              System
            </div>
          );
        }
        
        // Create custom options with aiCategory first in "Suggested" section if it exists
        const customOptions = React.useMemo(() => {
          const options = [...categoryOptions];

          if (row.aiCategory && row.aiCategory !== '') {
            // Find the AI category option
            const aiCategoryOption = options.find(opt => opt.value === row.aiCategory);
            if (aiCategoryOption) {
              // Remove it from the main list
              const filteredOptions = options.filter(opt => opt.value !== row.aiCategory);
              // Create grouped options with AI suggestion first
              return [
                {
                  label: "Suggested",
                  options: [aiCategoryOption]
                },
                {
                  label: "All Categories",
                  options: filteredOptions
                }
              ];
            }
          }

          return options;
        }, [row.aiCategory]);

        return (
          <div className="flex">
            <InlineEditableSelect
              value={categoryOptions.find(opt => opt.value === (row.category ?? ''))}
              onSave={(value) => saveFieldRef.current(row.targetField, {
                category: value.value,
                autoCategorized: false,
              })}
              options={customOptions}
              className="min-w-72"
              label="Category"
              description={L.admin_panel.settings.manage_fields_table.select_a_category}
              placeholder=""
              hideLabel
              anchor="bottom start"
              customIcon={row.autoCategorized && row.category === row.aiCategory && row.aiCategory !== '' ? (
                <Tooltip text={L.admin_panel.settings.manage_fields_table.categorized_by_ai}>
                  <Icon name="SparklesIcon" className="w-4 h-4 text-purple-800" type="solid" />
                </Tooltip>
              ) : undefined}
              customButtonTextRenderer={(value: string) => {
                // The value here is the selected option's value, we need to get its label
                const selectedOption = categoryOptions.find(opt => opt.value === value);
                return highlightText(selectedOption?.label || '', info.searchTerm || '');
              }}
            />
          </div>
        );
      },
    },
    {
      accessorKey: "displayName",
      header: L.admin_panel.settings.manage_fields_table.display_name,
      size: 300,
      cell: (info: any) => {
        const row = info.row.original as DataTableRow;
        return (
          <InlineEditableText
            value={row.displayName || undefined}
            onSave={(value) => saveFieldRef.current(row.targetField, { displayName: String(value) })}
            placeholder={L.admin_panel.settings.manage_fields_table.enter_display_name}
            label={L.admin_panel.settings.manage_fields_table.display_name}
            className="max-w-xs"
            multiline
            autosize={false}
            displayRenderer={(value) => highlightText(String(value), info.searchTerm || '')}
            // Manually block UID as a special case
            disabled={row.targetField === 'uid'}
          />
        );
      },
    },
    {
      accessorKey: "description",
      header: L.admin_panel.settings.manage_fields_table.description,
      size: 300,
      cell: (info: any) => {
        const row = info.row.original as DataTableRow;
        return (
          <InlineEditableText
            value={row.description || ''}
            onSave={(value) => saveFieldRef.current(row.targetField, { description: String(value) })}
            placeholder={L.admin_panel.settings.manage_fields_table.enter_description}
            label={L.admin_panel.settings.manage_fields_table.description}
            multiline
            autosize={false}
            className="max-w-xs"
            displayRenderer={(value) => highlightText(String(value), info.searchTerm || '')}
            // Manually block UID as a special case
            disabled={row.targetField === 'uid'}
          />
        );
      },
    },
  ], [L]);


  return (
    <AdminPanelPage
      title={L.admin_panel.settings.manage_fields}
      breadcrumbs={breadcrumbs}
      keyboardShortcuts={[
        { key: '/', description: "Search" },
        { key: 'B', description: "Toggle sidebar", modifier: 'meta' },
        { key: 'K', description: L.admin_panel.command_palette.keyboard_shortcut, modifier: 'meta' },
      ]}
    >
      <div className="flex flex-col gap-y-1 w-full h-full">
        <Card className="rounded-md">
          <CardBody>
            <DataTable
              key="manage-fields-table"
              getRowId={getRowId}
              columns={columns}
              data={transformedFields}
              enableSearch
              className="min-h-48"
              pageSize={100}
              initialSorting={[{ id: 'targetField', desc: false }]}
              globalFilterFn={filterTargetFieldRows}
              verticalAlign="top"
              loading={isFieldsLoading}
            />
          </CardBody>
        </Card>
      </div>
    </AdminPanelPage>
  )
}